import React, { useState, useRef } from 'react'
import { Modal, Form, Button, Row, Col, Badge, Accordion } from 'react-bootstrap'
import { StyledModal } from './styles'
import SynthSelectionMap, { SynthSelectionMapHandle } from '../../views/subviews/SynthSelectionMap'
import styled from 'styled-components'

const ContentWrapper = styled.div`
  display: flex;
  gap: 20px;
  height: 100%;
`

const FormWrapper = styled.div`
  flex: 1;
  min-width: 400px;
  overflow-y: auto;
  padding-right: 20px;

  .text-muted {
    color: rgba(255, 255, 255, 0.6) !important;
  }

  .form-text {
    color: rgba(255, 255, 255, 0.6) !important;
  }

  .form-control {
    &::placeholder {
      color: rgba(255, 255, 255, 0.5) !important;
    }
  }
`

const MapWrapper = styled.div`
  flex: 1;
  min-width: 500px;
  border-radius: 8px;
  overflow: hidden;
  background: rgba(255, 255, 255, 0.05);
  height: 100%;
`

const StyledCloseButton = styled(Modal.Header)`
  .btn-close {
    filter: brightness(0) invert(1);
    opacity: 0.7;

    &:hover {
      opacity: 1;
    }
  }
`

const CheckboxGrid = styled.div`
  display: grid;
  grid-template-columns: repeat(auto-fill, minmax(200px, 1fr));
  gap: 12px;
  padding: 15px;
  background: rgba(255, 255, 255, 0.05);
  border-radius: 8px;
  margin-top: 5px;

  .form-check {
    margin: 0;
    padding: 8px 16px;
    border-radius: 6px;
    transition: background-color 0.2s;
    cursor: pointer;
    user-select: none;
    display: flex;
    align-items: center;
    gap: 8px;

    &:hover {
      background: rgba(255, 255, 255, 0.1);
    }

    .form-check-input {
      margin: 0;
      flex-shrink: 0;
    }

    .form-check-label {
      cursor: pointer;
      margin: 0;
      display: flex;
      align-items: center;
      gap: 8px;
      white-space: nowrap;
    }
  }
`

const SectionTitle = styled.div`
  display: flex;
  align-items: center;
  gap: 10px;
  margin-bottom: 15px;
  padding: 0 4px;

  h5 {
    margin: 0;
  }

  .selected-count {
    font-size: 0.9em;
    color: rgba(255, 255, 255, 0.6);
  }
`

const StyledAccordion = styled(Accordion)`
  .accordion-item {
    background: transparent;
    border: 1px solid rgba(255, 255, 255, 0.1);
    margin-bottom: 16px;
  }

  .accordion-button {
    background: rgba(255, 255, 255, 0.05);
    color: white;
    padding: 16px;

    &:not(.collapsed) {
      background: rgba(255, 255, 255, 0.1);
      color: white;
    }

    &:focus {
      box-shadow: none;
      border-color: rgba(255, 255, 255, 0.2);
    }

    &::after {
      filter: invert(1);
    }
  }

  .accordion-body {
    padding: 20px;
    background: rgba(0, 0, 0, 0.2);
  }
`

const BoundsContainer = styled.div`
  display: flex;
  gap: 20px;
  align-items: flex-start;

  .coordinates-container {
    flex: 1;
  }

  .preview-container {
    display: flex;
    flex-direction: column;
    gap: 10px;
    align-items: center;
  }
`

const FocusAreaButton = styled.button`
  width: 8rem;
  height: 8rem;
  border-radius: 8px;
  display: flex;
  align-items: center;
  margin-top: 2rem;
  justify-content: center;
  font-size: 1.1em;
  background: rgba(51, 136, 255, 0.1);
  border: 2px solid rgba(51, 136, 255, 0.3);
  color: #3388ff;
  transition: all 0.2s ease;
  cursor: pointer;

  &:hover:not(:disabled) {
    background: rgba(51, 136, 255, 0.2);
    border-color: rgba(51, 136, 255, 0.5);
  }

  &:disabled {
    cursor: not-allowed;
  }
`

interface SynthDataModalProps {
  show: boolean
  onHide: () => void
}

interface AssetEntry {
  assetType: string
  minAssets: string
  maxAssets: string
  distribution: string
  correlation: string
  spatialAutocorrelation: string
}

interface WeatherConditions {
  clear: boolean
  partlyCloudy: boolean
  overcast: boolean
  lightRain: boolean
  heavyRain: boolean
  fog: boolean
  snow: boolean
  haze: boolean
  dust: boolean
}

interface TimeOfDayOption {
  value: string
  icon: string
}

interface WeatherOption {
  key: keyof WeatherConditions
  label: string
  icon: string
}

interface FormDataType {
  // Source Data Parameters
  sourceType: 'satellite' | 'drone'
  areaOfInterest: string
  customBounds: {
    north: string
    south: string
    east: string
    west: string
  }
  sunAngle: string
  gsdLevel: string // Ground Sample Distance in meters
  lookdownAngle: string
  swathSize: string

  // Drone specific parameters
  altitude: string // meters
  elevation: string // meters above ground level
  range: string // meters

  // Output Specifications
  sampleCount: string
  outputFormat: string
  distributionPattern: string

  // Data Generation Rules
  assets: AssetEntry[]
  currentAsset: {
    assetType: string
    minAssets: string
    maxAssets: string
    distribution: string
    correlation: string
    spatialAutocorrelation: string
  }
  editingAssetIndex: number | null

  // Quality Control
  nullHandling: string
  edgeHandling: string
  outlierDetection: string

  // Advanced Options
  noiseLevel: string
  temporalEnabled: boolean
  temporalFrequency: string
  randomSeed: string

  // Environmental Conditions
  timeOfDay: string[]
  weatherConditions: WeatherConditions

  // Comments
  comments: string
}

export const SynthDataModal: React.FC<SynthDataModalProps> = ({ show, onHide }) => {
  const mapRef = useRef<SynthSelectionMapHandle>(null)
  const [formData, setFormData] = useState<FormDataType>({
    // Source Data Parameters
    sourceType: 'satellite',
    areaOfInterest: '',
    customBounds: {
      north: '',
      south: '',
      east: '',
      west: '',
    },
    sunAngle: '45',
    gsdLevel: '1',
    lookdownAngle: '90',
    swathSize: '10',

    // Drone specific parameters
    altitude: '100',
    elevation: '0',
    range: '1000',

    // Output Specifications
    sampleCount: '1000',
    outputFormat: 'GeoJSON',
    distributionPattern: 'random',

    // Data Generation Rules
    assets: [],
    currentAsset: {
      assetType: '',
      minAssets: '1',
      maxAssets: '10',
      distribution: 'normal',
      correlation: '0.8',
      spatialAutocorrelation: '0.5',
    },
    editingAssetIndex: null,

    // Quality Control
    nullHandling: 'exclude',
    edgeHandling: 'clip',
    outlierDetection: 'zscore',

    // Advanced Options
    noiseLevel: '0.1',
    temporalEnabled: false,
    temporalFrequency: 'daily',
    randomSeed: '',

    // Environmental Conditions
    timeOfDay: [],
    weatherConditions: {
      clear: false,
      partlyCloudy: false,
      overcast: false,
      lightRain: false,
      heavyRain: false,
      fog: false,
      snow: false,
      haze: false,
      dust: false,
    },

    // Comments
    comments: '',
  })

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault()

    // Create a formatted JSON object with all form data
    const jsonData = {
      sourceDataParameters: {
        sourceType: formData.sourceType,
        bounds: {
          north: parseFloat(formData.customBounds.north),
          south: parseFloat(formData.customBounds.south),
          east: parseFloat(formData.customBounds.east),
          west: parseFloat(formData.customBounds.west),
        },
        areaOfInterest: formData.areaOfInterest,
        sunAngle: parseFloat(formData.sunAngle),
        lookdownAngle: parseFloat(formData.lookdownAngle),
        swathSize: parseFloat(formData.swathSize),
        ...(formData.sourceType === 'satellite'
          ? {
              groundSampleDistance: parseFloat(formData.gsdLevel),
            }
          : {
              altitude: parseInt(formData.altitude),
              elevation: parseInt(formData.elevation),
              range: parseInt(formData.range),
            }),
      },
      outputSpecifications: {
        sampleCount: parseInt(formData.sampleCount),
        outputFormat: formData.outputFormat,
        distributionPattern: formData.distributionPattern,
      },
      dataGenerationRules: {
        assets: formData.assets.map((asset) => ({
          type: asset.assetType,
          minCount: parseInt(asset.minAssets),
          maxCount: parseInt(asset.maxAssets),
          distribution: asset.distribution,
          correlation: parseFloat(asset.correlation),
          spatialAutocorrelation: parseFloat(asset.spatialAutocorrelation),
        })),
      },
      environmentalConditions: {
        timeOfDay: formData.timeOfDay,
        weatherConditions: Object.entries(formData.weatherConditions)
          .filter(([_, value]) => value)
          .map(([key]) => key),
      },
      advancedOptions: {
        noiseLevel: parseFloat(formData.noiseLevel),
        randomSeed: formData.randomSeed ? parseInt(formData.randomSeed) : null,
        temporal: formData.temporalEnabled
          ? {
              enabled: true,
              frequency: formData.temporalFrequency,
            }
          : {
              enabled: false,
            },
      },
      comments: formData.comments,
    }

    // Create a Blob with the JSON data
    const blob = new Blob([JSON.stringify(jsonData, null, 2)], {
      type: 'application/json',
    })

    // Create a timestamp for the filename
    const timestamp = new Date().toISOString().replace(/[:.]/g, '-')

    // Create a download link and trigger it without mutating the DOM
    const objectUrl = URL.createObjectURL(blob)
    const downloadLink = document.createElement('a')
    downloadLink.href = objectUrl
    downloadLink.download = `synthetic-data-config-${timestamp}.json`
    downloadLink.click()

    // Clean up the URL object on the next tick to ensure the click has propagated
    setTimeout(() => URL.revokeObjectURL(objectUrl), 0)

    console.log('Generated configuration:', jsonData)
  }

  const handleBoundsChange = (
    bounds: { north: string; south: string; east: string; west: string } | null
  ) => {
    setFormData((prev) => ({
      ...prev,
      customBounds: bounds || {
        north: '',
        south: '',
        east: '',
        west: '',
      },
    }))
  }

  const handleClearBounds = () => {
    if (mapRef.current) {
      mapRef.current.clearBounds()
    }
  }

  const handleAddOrUpdateAsset = () => {
    if (!formData.currentAsset.assetType) return

    setFormData((prev) => {
      if (prev.editingAssetIndex !== null) {
        // Update existing asset
        const updatedAssets = [...prev.assets]
        updatedAssets[prev.editingAssetIndex] = { ...prev.currentAsset }
        return {
          ...prev,
          assets: updatedAssets,
          currentAsset: {
            assetType: '',
            minAssets: '1',
            maxAssets: '10',
            distribution: 'normal',
            correlation: '0.8',
            spatialAutocorrelation: '0.5',
          },
          editingAssetIndex: null,
        }
      } else {
        // Add new asset
        return {
          ...prev,
          assets: [...prev.assets, { ...prev.currentAsset }],
          currentAsset: {
            assetType: '',
            minAssets: '1',
            maxAssets: '10',
            distribution: 'normal',
            correlation: '0.8',
            spatialAutocorrelation: '0.5',
          },
        }
      }
    })
  }

  const handleEditAsset = (index: number) => {
    setFormData((prev) => ({
      ...prev,
      currentAsset: { ...prev.assets[index] },
      editingAssetIndex: index,
    }))
  }

  const handleCancelEdit = () => {
    setFormData((prev) => ({
      ...prev,
      currentAsset: {
        assetType: '',
        minAssets: '1',
        maxAssets: '10',
        distribution: 'normal',
        correlation: '0.8',
        spatialAutocorrelation: '0.5',
      },
      editingAssetIndex: null,
    }))
  }

  const handleRemoveAsset = (index: number) => {
    if (formData.editingAssetIndex === index) {
      // If removing the asset being edited, reset the form
      handleCancelEdit()
    }
    setFormData((prev) => ({
      ...prev,
      assets: prev.assets.filter((_, i) => i !== index),
    }))
  }

  const handlePreviewClick = () => {
    if (mapRef.current) {
      mapRef.current.focusBounds()
    }
  }

  const handleConfigLoad = (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0]
    if (!file) return

    const reader = new FileReader()
    reader.onload = (event) => {
      try {
        const config = JSON.parse(event.target?.result as string)

        // Update form data with loaded configuration
        setFormData((prev) => ({
          ...prev,
          sourceType: config.sourceDataParameters.sourceType,
          customBounds: {
            north: config.sourceDataParameters.bounds.north.toString(),
            south: config.sourceDataParameters.bounds.south.toString(),
            east: config.sourceDataParameters.bounds.east.toString(),
            west: config.sourceDataParameters.bounds.west.toString(),
          },
          sunAngle: config.sourceDataParameters.sunAngle?.toString() || prev.sunAngle,
          gsdLevel: config.sourceDataParameters.groundSampleDistance?.toString() || prev.gsdLevel,
          lookdownAngle:
            config.sourceDataParameters.lookdownAngle?.toString() || prev.lookdownAngle,
          swathSize: config.sourceDataParameters.swathSize?.toString() || prev.swathSize,
          altitude: config.sourceDataParameters.altitude?.toString() || prev.altitude,
          elevation: config.sourceDataParameters.elevation?.toString() || prev.elevation,
          range: config.sourceDataParameters.range?.toString() || prev.range,
          sampleCount: config.outputSpecifications.sampleCount.toString(),
          outputFormat: config.outputSpecifications.outputFormat,
          distributionPattern: config.outputSpecifications.distributionPattern,
          assets: config.dataGenerationRules.assets.map((asset: any) => ({
            assetType: asset.type,
            minAssets: asset.minCount.toString(),
            maxAssets: asset.maxCount.toString(),
            distribution: asset.distribution,
            correlation: asset.correlation.toString(),
            spatialAutocorrelation: asset.spatialAutocorrelation.toString(),
          })),
          timeOfDay: config.environmentalConditions.timeOfDay,
          weatherConditions: {
            clear: false,
            partlyCloudy: false,
            overcast: false,
            lightRain: false,
            heavyRain: false,
            fog: false,
            snow: false,
            haze: false,
            dust: false,
            ...config.environmentalConditions.weatherConditions.reduce(
              (acc: any, condition: string) => ({
                ...acc,
                [condition]: true,
              }),
              {}
            ),
          },
          noiseLevel: config.advancedOptions.noiseLevel.toString(),
          randomSeed: config.advancedOptions.randomSeed?.toString() || '',
          temporalEnabled: config.advancedOptions.temporal.enabled,
          temporalFrequency: config.advancedOptions.temporal.enabled
            ? config.advancedOptions.temporal.frequency
            : prev.temporalFrequency,
          areaOfInterest: config.environmentalConditions.areaOfInterest,
        }))

        // Update the map with the loaded bounds
        if (config.sourceDataParameters.bounds && mapRef.current) {
          // First clear any existing bounds
          mapRef.current.clearBounds()

          // Create a small delay to ensure the map is ready
          setTimeout(() => {
            // Trigger bounds drawing with the loaded coordinates
            const bounds = {
              north: config.sourceDataParameters.bounds.north.toString(),
              south: config.sourceDataParameters.bounds.south.toString(),
              east: config.sourceDataParameters.bounds.east.toString(),
              west: config.sourceDataParameters.bounds.west.toString(),
            }

            // Update the map with the new bounds
            mapRef.current?.setBounds?.(bounds)

            // Focus the map on the bounds
            mapRef.current?.focusBounds()
          }, 100)
        }

        console.log('Configuration loaded successfully')
      } catch (error) {
        console.error('Error loading configuration:', error)
      }
    }
    reader.readAsText(file)

    // Reset the file input
    e.target.value = ''
  }

  return (
    <StyledModal show={show} onHide={onHide} size="lg">
      <StyledCloseButton closeButton>
        <Modal.Title>Synthetic Dataset Request Form</Modal.Title>
      </StyledCloseButton>
      <Modal.Body>
        <Form onSubmit={handleSubmit}>
          <ContentWrapper>
            <FormWrapper>
              <StyledAccordion defaultActiveKey="0">
                <Accordion.Item eventKey="0">
                  <Accordion.Header>Source Data Parameters</Accordion.Header>
                  <Accordion.Body>
                    <Row className="mb-3">
                      <Col md={12}>
                        <Form.Group>
                          <Form.Label>Source Type</Form.Label>
                          <Form.Select
                            value={formData.sourceType}
                            onChange={(e) =>
                              setFormData({
                                ...formData,
                                sourceType: e.target.value as 'satellite' | 'drone',
                              })
                            }
                          >
                            <option value="satellite">Satellite Imagery</option>
                            <option value="drone">Drone Imagery</option>
                          </Form.Select>
                        </Form.Group>
                      </Col>
                    </Row>

                    <Row className="mb-3">
                      <Col md={12}>
                        <Form.Group>
                          <Form.Label>Area of Interest (AOI)</Form.Label>
                          <Form.Control
                            type="text"
                            placeholder="Enter location name or description"
                            value={formData.areaOfInterest || ''}
                            onChange={(e) =>
                              setFormData({
                                ...formData,
                                areaOfInterest: e.target.value,
                              })
                            }
                          />
                          <Form.Text className="text-muted">
                            Describe the area you want to analyze (e.g., "Downtown Seattle", "Port
                            of Los Angeles")
                          </Form.Text>
                        </Form.Group>
                      </Col>
                    </Row>

                    <Row className="mb-3">
                      <Col md={6}>
                        <Button
                          variant="outline-primary"
                          size="sm"
                          onClick={handleClearBounds}
                          className="w-100"
                          style={{
                            borderColor: '#3388ff',
                            color: '#3388ff',
                          }}
                        >
                          Clear Bounds & Draw New
                        </Button>
                      </Col>
                      <Col md={6}>
                        <Button
                          variant="primary"
                          size="sm"
                          onClick={() => mapRef.current?.startDrawing()}
                          className="w-100"
                          style={{
                            backgroundColor: '#3388ff',
                            borderColor: '#3388ff',
                          }}
                        >
                          Draw Bounds
                        </Button>
                      </Col>
                    </Row>

                    <BoundsContainer>
                      <div className="coordinates-container">
                        <Row className="mb-3">
                          <Col md={6}>
                            <Form.Group>
                              <Form.Label>North</Form.Label>
                              <Form.Control
                                type="text"
                                value={formData.customBounds.north}
                                onChange={(e) =>
                                  setFormData({
                                    ...formData,
                                    customBounds: {
                                      ...formData.customBounds,
                                      north: e.target.value,
                                    },
                                  })
                                }
                                placeholder="Latitude"
                              />
                            </Form.Group>
                          </Col>
                          <Col md={6}>
                            <Form.Group>
                              <Form.Label>South</Form.Label>
                              <Form.Control
                                type="text"
                                value={formData.customBounds.south}
                                onChange={(e) =>
                                  setFormData({
                                    ...formData,
                                    customBounds: {
                                      ...formData.customBounds,
                                      south: e.target.value,
                                    },
                                  })
                                }
                                placeholder="Latitude"
                              />
                            </Form.Group>
                          </Col>
                        </Row>

                        <Row className="mb-3">
                          <Col md={6}>
                            <Form.Group>
                              <Form.Label>East</Form.Label>
                              <Form.Control
                                type="text"
                                value={formData.customBounds.east}
                                onChange={(e) =>
                                  setFormData({
                                    ...formData,
                                    customBounds: {
                                      ...formData.customBounds,
                                      east: e.target.value,
                                    },
                                  })
                                }
                                placeholder="Longitude"
                              />
                            </Form.Group>
                          </Col>
                          <Col md={6}>
                            <Form.Group>
                              <Form.Label>West</Form.Label>
                              <Form.Control
                                type="text"
                                value={formData.customBounds.west}
                                onChange={(e) =>
                                  setFormData({
                                    ...formData,
                                    customBounds: {
                                      ...formData.customBounds,
                                      west: e.target.value,
                                    },
                                  })
                                }
                                placeholder="Longitude"
                              />
                            </Form.Group>
                          </Col>
                        </Row>
                      </div>

                      <div className="preview-container">
                        {formData.customBounds.north ? (
                          <>
                            <FocusAreaButton type="button" onClick={handlePreviewClick}>
                              Focus Selected Area
                            </FocusAreaButton>
                            <small className="text-muted pb-2">
                              Click to center map on selected area
                            </small>
                          </>
                        ) : (
                          <FocusAreaButton type="button" disabled style={{ opacity: 0.5 }}>
                            No Area Selected
                          </FocusAreaButton>
                        )}
                      </div>
                    </BoundsContainer>

                    {formData.sourceType === 'satellite' ? (
                      <Row className="mb-3">
                        <Col md={3}>
                          <Form.Group>
                            <Form.Label>Sun Angle (°)</Form.Label>
                            <Form.Control
                              type="number"
                              min="0"
                              max="90"
                              value={formData.sunAngle}
                              onChange={(e) =>
                                setFormData({
                                  ...formData,
                                  sunAngle: e.target.value,
                                })
                              }
                              placeholder="Enter sun angle"
                            />
                            <Form.Text className="text-muted">
                              Angle of sun relative to horizon
                            </Form.Text>
                          </Form.Group>
                        </Col>
                        <Col md={3}>
                          <Form.Group>
                            <Form.Label>GSD (m)</Form.Label>
                            <Form.Control
                              type="number"
                              min="0.1"
                              step="0.1"
                              value={formData.gsdLevel}
                              onChange={(e) =>
                                setFormData({
                                  ...formData,
                                  gsdLevel: e.target.value,
                                })
                              }
                              placeholder="Enter GSD"
                            />
                            <Form.Text className="text-muted">Ground Sample Distance</Form.Text>
                          </Form.Group>
                        </Col>
                        <Col md={3}>
                          <Form.Group>
                            <Form.Label>Lookdown Angle (°)</Form.Label>
                            <Form.Control
                              type="number"
                              min="0"
                              max="90"
                              value={formData.lookdownAngle}
                              onChange={(e) =>
                                setFormData({
                                  ...formData,
                                  lookdownAngle: e.target.value,
                                })
                              }
                              placeholder="Enter lookdown angle"
                            />
                            <Form.Text className="text-muted">Sensor angle from nadir</Form.Text>
                          </Form.Group>
                        </Col>
                        <Col md={3}>
                          <Form.Group>
                            <Form.Label>Swath Size (km)</Form.Label>
                            <Form.Control
                              type="number"
                              min="1"
                              value={formData.swathSize}
                              onChange={(e) =>
                                setFormData({
                                  ...formData,
                                  swathSize: e.target.value,
                                })
                              }
                              placeholder="Enter swath size"
                            />
                            <Form.Text className="text-muted">Image width in kilometers</Form.Text>
                          </Form.Group>
                        </Col>
                      </Row>
                    ) : (
                      <Row className="mb-3">
                        <Col md={4}>
                          <Form.Group>
                            <Form.Label>Flight Altitude</Form.Label>
                            <Form.Control
                              type="number"
                              value={formData.altitude}
                              onChange={(e) =>
                                setFormData({
                                  ...formData,
                                  altitude: e.target.value,
                                })
                              }
                              placeholder="Enter altitude in meters"
                            />
                            <Form.Text className="text-muted">
                              Height above ground in meters
                            </Form.Text>
                          </Form.Group>
                        </Col>
                        <Col md={4}>
                          <Form.Group>
                            <Form.Label>Ground Elevation</Form.Label>
                            <Form.Control
                              type="number"
                              value={formData.elevation}
                              onChange={(e) =>
                                setFormData({
                                  ...formData,
                                  elevation: e.target.value,
                                })
                              }
                              placeholder="Enter elevation in meters"
                            />
                            <Form.Text className="text-muted">Ground level elevation</Form.Text>
                          </Form.Group>
                        </Col>
                        <Col md={4}>
                          <Form.Group>
                            <Form.Label>Flight Range</Form.Label>
                            <Form.Control
                              type="number"
                              value={formData.range}
                              onChange={(e) =>
                                setFormData({
                                  ...formData,
                                  range: e.target.value,
                                })
                              }
                              placeholder="Enter range in meters"
                            />
                            <Form.Text className="text-muted">Coverage radius</Form.Text>
                          </Form.Group>
                        </Col>
                      </Row>
                    )}
                  </Accordion.Body>
                </Accordion.Item>

                <Accordion.Item eventKey="1">
                  <Accordion.Header>Output Specifications</Accordion.Header>
                  <Accordion.Body>
                    <Row className="mb-3">
                      <Col md={4}>
                        <Form.Group>
                          <Form.Label>Sample Count</Form.Label>
                          <Form.Control
                            type="number"
                            value={formData.sampleCount}
                            onChange={(e) =>
                              setFormData({
                                ...formData,
                                sampleCount: e.target.value,
                              })
                            }
                          />
                        </Form.Group>
                      </Col>
                      <Col md={4}>
                        <Form.Group>
                          <Form.Label>Output Format</Form.Label>
                          <Form.Select
                            value={formData.outputFormat}
                            onChange={(e) =>
                              setFormData({
                                ...formData,
                                outputFormat: e.target.value,
                              })
                            }
                          >
                            <option value="GeoJSON">GeoJSON</option>
                            <option value="CSV">CSV</option>
                            <option value="Shapefile">Shapefile</option>
                          </Form.Select>
                        </Form.Group>
                      </Col>
                      <Col md={4}>
                        <Form.Group>
                          <Form.Label>Distribution Pattern</Form.Label>
                          <Form.Select
                            value={formData.distributionPattern}
                            onChange={(e) =>
                              setFormData({
                                ...formData,
                                distributionPattern: e.target.value,
                              })
                            }
                          >
                            <option value="random">Random</option>
                            <option value="grid">Grid-based</option>
                            <option value="stratified">Stratified</option>
                            <option value="clustered">Clustered</option>
                          </Form.Select>
                        </Form.Group>
                      </Col>
                    </Row>
                  </Accordion.Body>
                </Accordion.Item>

                <Accordion.Item eventKey="2">
                  <Accordion.Header>3D Assets</Accordion.Header>
                  <Accordion.Body>
                    <Row className="mb-3">
                      <Col md={12}>
                        <Form.Group>
                          <Form.Label>Asset Type</Form.Label>
                          <Form.Control
                            type="text"
                            value={formData.currentAsset.assetType}
                            onChange={(e) =>
                              setFormData((prev) => ({
                                ...prev,
                                currentAsset: {
                                  ...prev.currentAsset,
                                  assetType: e.target.value,
                                },
                              }))
                            }
                            placeholder="Enter asset type (e.g., 'Solar Panel', 'Wind Turbine')"
                          />
                        </Form.Group>
                      </Col>
                    </Row>
                    <Row className="mb-3">
                      <Col md={6}>
                        <Form.Group>
                          <Form.Label># of Assets (Min)</Form.Label>
                          <Form.Control
                            type="number"
                            min="1"
                            value={formData.currentAsset.minAssets}
                            onChange={(e) =>
                              setFormData((prev) => ({
                                ...prev,
                                currentAsset: {
                                  ...prev.currentAsset,
                                  minAssets: e.target.value,
                                },
                              }))
                            }
                          />
                        </Form.Group>
                      </Col>
                      <Col md={6}>
                        <Form.Group>
                          <Form.Label># of Assets (Max)</Form.Label>
                          <Form.Control
                            type="number"
                            min="1"
                            value={formData.currentAsset.maxAssets}
                            onChange={(e) =>
                              setFormData((prev) => ({
                                ...prev,
                                currentAsset: {
                                  ...prev.currentAsset,
                                  maxAssets: e.target.value,
                                },
                              }))
                            }
                          />
                        </Form.Group>
                      </Col>
                    </Row>
                    <Row className="mb-3">
                      <Col md={4}>
                        <Form.Group>
                          <Form.Label>Distribution Type</Form.Label>
                          <Form.Select
                            value={formData.currentAsset.distribution}
                            onChange={(e) =>
                              setFormData((prev) => ({
                                ...prev,
                                currentAsset: {
                                  ...prev.currentAsset,
                                  distribution: e.target.value,
                                },
                              }))
                            }
                          >
                            <option value="normal">Normal</option>
                            <option value="uniform">Uniform</option>
                            <option value="lognormal">Log-normal</option>
                          </Form.Select>
                        </Form.Group>
                      </Col>
                      <Col md={4}>
                        <Form.Group>
                          <Form.Label>Correlation Strength</Form.Label>
                          <Form.Control
                            type="number"
                            step="0.1"
                            min="0"
                            max="1"
                            value={formData.currentAsset.correlation}
                            onChange={(e) =>
                              setFormData((prev) => ({
                                ...prev,
                                currentAsset: {
                                  ...prev.currentAsset,
                                  correlation: e.target.value,
                                },
                              }))
                            }
                          />
                        </Form.Group>
                      </Col>
                      <Col md={4}>
                        <Form.Group>
                          <Form.Label>Spatial Autocorrelation</Form.Label>
                          <Form.Control
                            type="number"
                            step="0.1"
                            min="0"
                            max="1"
                            value={formData.currentAsset.spatialAutocorrelation}
                            onChange={(e) =>
                              setFormData((prev) => ({
                                ...prev,
                                currentAsset: {
                                  ...prev.currentAsset,
                                  spatialAutocorrelation: e.target.value,
                                },
                              }))
                            }
                          />
                        </Form.Group>
                      </Col>
                    </Row>
                    <Row className="mb-3">
                      <Col className="d-flex gap-2">
                        <Button
                          variant="primary"
                          onClick={handleAddOrUpdateAsset}
                          disabled={!formData.currentAsset.assetType}
                        >
                          {formData.editingAssetIndex !== null ? 'Update Asset' : 'Add Asset'}
                        </Button>
                        {formData.editingAssetIndex !== null && (
                          <Button variant="secondary" onClick={handleCancelEdit}>
                            Cancel
                          </Button>
                        )}
                      </Col>
                    </Row>
                    {formData.assets.length > 0 && (
                      <Row>
                        <Col>
                          <h6
                            style={{
                              color: 'rgba(255, 255, 255, 0.9)',
                              marginBottom: '12px',
                              fontSize: '0.95em',
                              fontWeight: '500',
                            }}
                          >
                            Added Assets:
                          </h6>
                          <div
                            style={{
                              display: 'flex',
                              flexWrap: 'wrap',
                              gap: '10px',
                              marginTop: '10px',
                            }}
                          >
                            {formData.assets.map((asset, index) => (
                              <Badge
                                key={index}
                                bg={formData.editingAssetIndex === index ? 'primary' : 'secondary'}
                                style={{
                                  padding: '8px 12px',
                                  display: 'flex',
                                  alignItems: 'center',
                                  gap: '8px',
                                  cursor: 'pointer',
                                }}
                                onClick={() => handleEditAsset(index)}
                              >
                                {asset.assetType} ({asset.minAssets}-{asset.maxAssets})
                                <Button
                                  variant="link"
                                  size="sm"
                                  style={{
                                    padding: '0',
                                    color: 'white',
                                    textDecoration: 'none',
                                  }}
                                  onClick={(e) => {
                                    e.stopPropagation()
                                    handleRemoveAsset(index)
                                  }}
                                >
                                  ×
                                </Button>
                              </Badge>
                            ))}
                          </div>
                        </Col>
                      </Row>
                    )}
                  </Accordion.Body>
                </Accordion.Item>

                <Accordion.Item eventKey="3">
                  <Accordion.Header>Environmental Conditions</Accordion.Header>
                  <Accordion.Body>
                    <Row className="mb-4">
                      <Col md={12}>
                        <Form.Group>
                          <SectionTitle>
                            <Form.Label className="mb-0">Time of Day</Form.Label>
                            <span className="selected-count">
                              {formData.timeOfDay.length} selected
                            </span>
                          </SectionTitle>
                          <CheckboxGrid>
                            {(
                              [
                                { value: 'Dawn', icon: '🌅' },
                                { value: 'Morning', icon: '🌄' },
                                { value: 'Noon', icon: '☀️' },
                                { value: 'Afternoon', icon: '🌤' },
                                { value: 'Evening', icon: '🌆' },
                                { value: 'Dusk', icon: '🌇' },
                                { value: 'Night', icon: '🌙' },
                                { value: 'Golden Hour', icon: '✨' },
                                { value: 'Blue Hour', icon: '💫' },
                              ] as TimeOfDayOption[]
                            ).map(({ value, icon }) => (
                              <Form.Check
                                key={value}
                                type="checkbox"
                                id={`timeOfDay-${value}`}
                                label={
                                  <span>
                                    {icon} {value}
                                  </span>
                                }
                                checked={formData.timeOfDay.includes(value)}
                                onChange={(e) => {
                                  setFormData((prev) => ({
                                    ...prev,
                                    timeOfDay: e.target.checked
                                      ? [...prev.timeOfDay, value]
                                      : prev.timeOfDay.filter((t) => t !== value),
                                  }))
                                }}
                              />
                            ))}
                          </CheckboxGrid>
                        </Form.Group>
                      </Col>
                    </Row>

                    <Row>
                      <Col md={12}>
                        <Form.Group>
                          <SectionTitle>
                            <Form.Label className="mb-0">Weather Conditions</Form.Label>
                            <span className="selected-count">
                              {Object.values(formData.weatherConditions).filter(Boolean).length}{' '}
                              selected
                            </span>
                          </SectionTitle>
                          <CheckboxGrid>
                            {(
                              [
                                {
                                  key: 'clear',
                                  label: 'Clear Sky',
                                  icon: '☀️',
                                },
                                {
                                  key: 'partlyCloudy',
                                  label: 'Partly Cloudy',
                                  icon: '⛅',
                                },
                                {
                                  key: 'overcast',
                                  label: 'Overcast',
                                  icon: '☁️',
                                },
                                {
                                  key: 'lightRain',
                                  label: 'Light Rain',
                                  icon: '🌦',
                                },
                                {
                                  key: 'heavyRain',
                                  label: 'Heavy Rain',
                                  icon: '🌧',
                                },
                                { key: 'fog', label: 'Fog', icon: '🌫' },
                                { key: 'snow', label: 'Snow', icon: '🌨' },
                                { key: 'haze', label: 'Haze', icon: '😶‍🌫️' },
                                { key: 'dust', label: 'Dust', icon: '💨' },
                              ] as WeatherOption[]
                            ).map(({ key, label, icon }) => (
                              <Form.Check
                                key={key}
                                type="checkbox"
                                id={`weather-${key}`}
                                label={
                                  <span>
                                    {icon} {label}
                                  </span>
                                }
                                checked={formData.weatherConditions[key]}
                                onChange={(e) => {
                                  setFormData((prev) => ({
                                    ...prev,
                                    weatherConditions: {
                                      ...prev.weatherConditions,
                                      [key]: e.target.checked,
                                    },
                                  }))
                                }}
                              />
                            ))}
                          </CheckboxGrid>
                        </Form.Group>
                      </Col>
                    </Row>
                  </Accordion.Body>
                </Accordion.Item>

                <Accordion.Item eventKey="4">
                  <Accordion.Header>Advanced Options</Accordion.Header>
                  <Accordion.Body>
                    <Row className="mb-3">
                      <Col md={6}>
                        <Form.Group>
                          <Form.Label>Noise Level</Form.Label>
                          <Form.Control
                            type="number"
                            step="0.1"
                            min="0"
                            max="1"
                            value={formData.noiseLevel}
                            onChange={(e) =>
                              setFormData({
                                ...formData,
                                noiseLevel: e.target.value,
                              })
                            }
                          />
                        </Form.Group>
                      </Col>
                      <Col md={6}>
                        <Form.Group>
                          <Form.Label>Random Seed</Form.Label>
                          <Form.Control
                            type="number"
                            value={formData.randomSeed}
                            onChange={(e) =>
                              setFormData({
                                ...formData,
                                randomSeed: e.target.value,
                              })
                            }
                            placeholder="Optional"
                          />
                        </Form.Group>
                      </Col>
                    </Row>
                    <Row>
                      <Col md={6}>
                        <Form.Group>
                          <Form.Check
                            type="switch"
                            label="Enable Temporal Generation"
                            checked={formData.temporalEnabled}
                            onChange={(e) =>
                              setFormData({
                                ...formData,
                                temporalEnabled: e.target.checked,
                              })
                            }
                          />
                        </Form.Group>
                      </Col>
                      {formData.temporalEnabled && (
                        <Col md={6}>
                          <Form.Group>
                            <Form.Label>Temporal Frequency</Form.Label>
                            <Form.Select
                              value={formData.temporalFrequency}
                              onChange={(e) =>
                                setFormData({
                                  ...formData,
                                  temporalFrequency: e.target.value,
                                })
                              }
                            >
                              <option value="hourly">Hourly</option>
                              <option value="daily">Daily</option>
                              <option value="weekly">Weekly</option>
                              <option value="monthly">Monthly</option>
                            </Form.Select>
                          </Form.Group>
                        </Col>
                      )}
                    </Row>
                  </Accordion.Body>
                </Accordion.Item>

                <Accordion.Item eventKey="5">
                  <Accordion.Header>Comments</Accordion.Header>
                  <Accordion.Body>
                    <Row>
                      <Col md={12}>
                        <Form.Group>
                          <Form.Control
                            as="textarea"
                            rows={4}
                            placeholder="Add any additional notes, requirements, or special instructions..."
                            value={formData.comments}
                            onChange={(e) =>
                              setFormData({
                                ...formData,
                                comments: e.target.value,
                              })
                            }
                            style={{
                              resize: 'vertical',
                              minHeight: '100px',
                              backgroundColor: 'rgba(255, 255, 255, 0.05)',
                              color: 'white',
                              border: '1px solid rgba(255, 255, 255, 0.1)',
                            }}
                          />
                        </Form.Group>
                      </Col>
                    </Row>
                  </Accordion.Body>
                </Accordion.Item>
              </StyledAccordion>
            </FormWrapper>
            <MapWrapper>
              <SynthSelectionMap ref={mapRef} onBoundsChange={handleBoundsChange} />
            </MapWrapper>
          </ContentWrapper>
        </Form>
      </Modal.Body>
      <Modal.Footer>
        <input
          type="file"
          id="config-file-input"
          accept=".json"
          style={{ display: 'none' }}
          onChange={handleConfigLoad}
        />
        <Button
          variant="outline-primary"
          onClick={() => document.getElementById('config-file-input')?.click()}
        >
          Load Config
        </Button>
        <Button variant="secondary" onClick={onHide}>
          Cancel
        </Button>
        <Button variant="primary" onClick={handleSubmit}>
          Generate
        </Button>
      </Modal.Footer>
    </StyledModal>
  )
}

export default SynthDataModal
